import { FormInputController } from '@wajeez-web/components/form-builder';
import { Autocomplete } from '@wajeez-web/components/mui';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { Server } from '@wajeez/wajeez.microservices';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  ComponentTypesActions,
  useContentDispatch,
  useContentSelector,
  CollectionComponetTypesActions,
} from '@wajeez-web/stores/content';
import { useEffect } from 'react';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';

interface AssignAllowedComponentTypesModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionComponentTypeId: string | undefined;
  onSubmit: () => void;
}

type FormInput = Server.SetAllowedComponentTypesInput;

export const AssignAllowedComponentTypesModal = ({
  isOpen,
  onClose,
  collectionComponentTypeId,
  onSubmit,
}: AssignAllowedComponentTypesModalProps) => {
  const contentDispatch = useContentDispatch();

  const componentTypesLookups = useContentSelector(
    ({ componentTypes }) => componentTypes.componentTypesLookups
  );
  const componentTypesByCollectionComponentTypeId = useContentSelector(
    ({ componentTypes }) =>
      componentTypes.componentTypesByCollectionComponentTypeId
  );

  const setAllowedComponentTypesStatus = useContentSelector(
    ({ collectionComponentTypes }) =>
      collectionComponentTypes.setAllowedComponentTypes.status
  );

  const { control, handleSubmit, reset } = useForm<FormInput>();

  const onFormSubmit: SubmitHandler<FormInput> = async (data) => {
    const mappedData: Server.SetAllowedComponentTypesInput = {
      ...data,
      allowedComponentTypeIds:
        data.allowedComponentTypeIds?.map((d) => Number(d)) || [],
    };

    const res = await contentDispatch(
      CollectionComponetTypesActions.setAllowedComponentTypesAsync({
        ...mappedData,
        collectionComponentTypeId: Number(collectionComponentTypeId),
      })
    );

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit();
    }
  };

  useEffect(() => {
    contentDispatch(
      ComponentTypesActions.getComponentTypesLookupsAsync({
        usageType: Server.ComponentUsageType.Component,
      })
    );
  }, []);

  useEffect(() => {
    if (collectionComponentTypeId) {
      contentDispatch(
        ComponentTypesActions.getComponentTypesByCollectionComponentTypeIdAsync(
          { collectionComponentTypeId: Number(collectionComponentTypeId) }
        )
      );
    }
  }, [collectionComponentTypeId]);

  useEffect(() => {
    if (componentTypesByCollectionComponentTypeId.data) {
      reset({
        allowedComponentTypeIds:
          componentTypesByCollectionComponentTypeId.data.map((d) => d.id),
      });
    }
  }, [componentTypesByCollectionComponentTypeId.data]);

  const isLoading =
    componentTypesLookups.status === 'loading' ||
    componentTypesByCollectionComponentTypeId.status === 'loading';

  const isLoadingSubmit = setAllowedComponentTypesStatus === 'loading';

  return (
    <Modal
      title="Assign Allowed Component Types"
      withScroll={false}
      open={isOpen}
      onClose={onClose}
      handleAlert={false}
      alertTarget="modal"
      fontClassName="font-['Tajawal']"
    >
      {isLoading ? (
        <div className="w-full h-[400px] flex justify-center items-center">
          <Spin />
        </div>
      ) : (
        <div className="w-full h-[400px]">
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            className="w-full h-full flex flex-col justify-between items-between font-['Tajawal']"
            noValidate
          >
            <div className="w-full h-full flex flex-col justify-start items-start space-y-3 overflow-y-auto">
              <div className="w-[500px]">
                <FormInputController
                  control={control}
                  name={`allowedComponentTypeIds`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                  }}
                  renderInput={({ ...props }) => (
                    <Autocomplete
                      id={`allowedComponentTypeIds`}
                      options={componentTypesLookups.data.map((t) => {
                        return {
                          value: t.id,
                          label: t.name,
                        };
                      })}
                      label="Component Types"
                      color="#1f7983"
                      multiple
                      heightClassName="h-auto"
                      {...props}
                    />
                  )}
                />
              </div>
            </div>
            <div className="mt-6 pb-6 w-full flex justify-end items-center">
              <div className="w-[90px]">
                <ButtonHeadless
                  type="submit"
                  paddingYStyle="py-1"
                  borderRadiusStyle="rounded-[6px]"
                >
                  <div className="w-full flex justify-center items-center text-white text-[16px] font-medium">
                    <ReturnTranslatedText
                      origintext="Save"
                      text={`buttons.${'Save'}`}
                    />
                    {isLoadingSubmit && (
                      <BiLoaderAlt className="mr-2 animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[90px] mr-4">
                <ButtonHeadless
                  paddingYStyle="py-1"
                  bgStyle="bg-warmGray-300"
                  borderRadiusStyle="rounded-[6px]"
                  action={onClose}
                >
                  <div className="w-full flex justify-center items-center text-warmGray-500 text-[16px] font-bold">
                    <ReturnTranslatedText
                      origintext="Cancel"
                      text={`buttons.${'Cancel'}`}
                    />
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
};
